import { useState } from 'react';
import { ChevronDown, Zap, Minus, Plus, Star, StarOff } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { ENERGY_TYPES } from '../utils/constants';

// Pack +/- counter with the energy/hit tracking disclosure. Shared by the
// set cards on SetGrid and the tracking panel on the cards page. Logging an
// energy (+ or +★) also counts a pack/hit at the set level — see the
// UPDATE_ENERGY_STAT rollup in CollectionContext.
const PackEnergyTracker = ({ set, energyDefaultExpanded = false }) => {
  const {
    getSetPackCount,
    incrementPackCount,
    decrementPackCount,
    incrementEnergy,
    decrementEnergy,
    decrementEnergyHit,
    getEnergyTypeStats,
  } = useCollection();

  const [energyExpanded, setEnergyExpanded] = useState(energyDefaultExpanded);
  const packCount = getSetPackCount(set.id);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {/* Pack counter */}
      <div className="bg-cream-bg border border-border rounded-lg px-2.5 py-2 flex items-center justify-between">
        <span className="text-[11px] font-medium text-ink-secondary">Packs</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => decrementPackCount(set.id, set)}
            disabled={packCount === 0}
            className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
              packCount === 0
                ? 'bg-border text-ink-faint cursor-not-allowed'
                : 'bg-danger-bg text-brick hover:bg-danger-border'
            }`}
          >
            <Minus size={12} />
          </button>
          <span className="text-[13px] font-bold text-ink-primary min-w-[20px] text-center">
            {packCount}
          </span>
          <button
            onClick={() => incrementPackCount(set.id, set)}
            disabled={packCount >= 999}
            className="w-6 h-6 rounded-md flex items-center justify-center bg-success-bg text-success-green hover:bg-success-border transition-colors"
          >
            <Plus size={12} />
          </button>
        </div>
      </div>

      {/* Energy tracking disclosure */}
      <div className="mt-2 border border-border rounded-lg overflow-hidden">
        <button
          onClick={() => setEnergyExpanded((prev) => !prev)}
          className="w-full flex items-center justify-between px-2.5 py-1.5 bg-cream-bg text-[11px] font-medium text-ink-secondary"
        >
          <span className="flex items-center gap-1">
            <Zap size={11} />
            Energy Tracking
          </span>
          <ChevronDown
            size={13}
            className={`transition-transform ${energyExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {energyExpanded && (
          <div className="px-2.5 py-2 bg-white">
            {ENERGY_TYPES.map((energy) => {
              const stats = getEnergyTypeStats(set.id, energy.name);
              const hitRate = stats.packs > 0 ? Math.round((stats.hits / stats.packs) * 100) : 0;

              return (
                <div
                  key={energy.name}
                  className="flex flex-col gap-1 text-[11px] py-1.5 border-b border-border last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: energy.color }}
                      />
                      <span className="font-medium text-ink-primary">{energy.name}</span>
                    </div>
                    <span
                      className={`text-[10px] ${
                        stats.hits > 0 ? 'text-success-green font-semibold' : 'text-ink-faint'
                      }`}
                    >
                      {stats.hits}/{stats.packs} hits ({hitRate}%)
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => decrementEnergy(set.id, energy.name)}
                      disabled={stats.packs === 0}
                      className={`w-7 h-7 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        stats.packs === 0 ? 'bg-border text-ink-faint' : 'bg-danger-bg text-brick'
                      }`}
                    >
                      <Minus size={9} />
                    </button>
                    <span className="min-w-[16px] text-center font-bold text-ink-primary">
                      {stats.packs}
                    </span>
                    <button
                      onClick={() => incrementEnergy(set.id, energy.name, false)}
                      className="w-7 h-7 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-success-bg text-success-green"
                    >
                      <Plus size={9} />
                    </button>
                    <button
                      onClick={() => incrementEnergy(set.id, energy.name, true)}
                      title="Add pack with hit"
                      className="w-7 h-7 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-amber/20 text-amber"
                    >
                      <Star size={9} />
                    </button>
                    <button
                      onClick={() => decrementEnergyHit(set.id, energy.name)}
                      disabled={stats.hits === 0}
                      title="Remove a mis-clicked hit (pack stays)"
                      className={`w-7 h-7 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        stats.hits === 0 ? 'bg-border text-ink-faint' : 'bg-amber/20 text-amber'
                      }`}
                    >
                      <StarOff size={9} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PackEnergyTracker;
